import React, { useState, useCallback, useEffect } from 'react';
import { Typography, Button, FormControlLabel, Radio, Box, Avatar } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { APP_VERSION, BUILD_DATE } from '../version';
import './Settings.css';

interface SettingsProps {
  open: boolean;
  onClose: () => void;
}

const Settings: React.FC<SettingsProps> = ({ open, onClose }) => {
  const { logout, user, organization } = useAuth();
  const [option1, setOption1] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      // Set visible after a short delay to trigger animation
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [open]);

  const handleLogout = useCallback(() => {
    setIsVisible(false);
    // Wait for the close animation, then logout
    setTimeout(() => {
      logout({ 
        logoutParams: {
          returnTo: window.location.origin 
        }
      });
    }, 300);
  }, [logout]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsVisible(false);
      // Wait for animation to finish before actually closing
      setTimeout(onClose, 300);
    }
  }, [onClose]);

  if (!open) return null;

  return (
    <div className={`settings-overlay ${isVisible ? 'visible' : ''}`} onClick={handleClose}>
      <div className="settings-modal">
        <Typography variant="h5" className="settings-title">Settings</Typography>
        
        <div className="settings-content">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Avatar
              src={user?.picture}
              alt={user?.name}
              sx={{ width: 64, height: 64, mb: 2 }}
            />
            <Typography variant="h6">{user?.name}</Typography>
            <Typography variant="body1" color="text.secondary">
              {user?.email}
            </Typography>
            {organization && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {organization.token} - {organization.organization_id}
              </Typography>
            )}
            {!organization && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No organization
              </Typography>
            )}
          </Box>

          <FormControlLabel
            control={
              <Radio
                checked={option1}
                onChange={(e) => setOption1(e.target.checked)}
                color="primary"
              />
            }
            label="Option 1"
          />
        </div>

        <div className="settings-actions">
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleLogout}
            className="logout-button"
          >
            Logout
          </Button>
        </div>

        <div className="settings-footer">
          <div className="footer-links">
            <a href="https://www.google.com" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            <a href="https://www.google.com" target="_blank" rel="noopener noreferrer">
              Terms of Use
            </a>
          </div>
          <div className="version-info">
            <Typography variant="caption" color="text.secondary">
              Version {APP_VERSION}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
